import { useEffect, useState, useCallback } from 'react';
import { Loader2, UserPlus, Crown, Shield, X, Search } from 'lucide-react';
import { apiClient } from '@/lib/api';
import { getInitials, type Workgroup, type WorkgroupMember } from './types';

interface Props {
  workgroup: Workgroup;
  canManage: boolean;
  currentUserId: string | null;
}

interface HrEntry {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  avatar_url: string | null;
  phone: string | null;
  extension: string | null;
  hr_department_name?: string | null;
  hr_position?: string | null;
}

interface UserSearchResult {
  id: string;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
}

export default function MembersTab({ workgroup, canManage, currentUserId }: Props) {
  const [members, setMembers] = useState<WorkgroupMember[]>([]);
  const [hrDirectory, setHrDirectory] = useState<HrEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [addOpen, setAddOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState<UserSearchResult[]>([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [wgRes, hrRes] = await Promise.all([
        apiClient.get<Workgroup & { members?: WorkgroupMember[] }>(`/messenger/workgroups/${workgroup.id}`),
        apiClient.get<HrEntry[]>(`/hr/employees?workgroup_id=${workgroup.id}`).catch(() => ({ data: [] })),
      ]);
      setMembers((wgRes.data as { members?: WorkgroupMember[] })?.members ?? []);
      setHrDirectory(hrRes.data ?? []);
    } finally {
      setLoading(false);
    }
  }, [workgroup.id]);

  useEffect(() => { load(); }, [load]);

  async function searchUsers(q: string) {
    setQuery(q);
    if (q.length < 2) return setSearchResults([]);
    const res = await apiClient.get<UserSearchResult[]>(`/messenger/users?search=${encodeURIComponent(q)}`);
    setSearchResults(res.data ?? []);
  }

  async function addMember(userId: string) {
    await apiClient.post(`/messenger/workgroups/${workgroup.id}/members`, { user_id: userId });
    setQuery('');
    setSearchResults([]);
    load();
  }

  async function removeMember(userId: string) {
    if (!confirm('Rimuovere il membro?')) return;
    await apiClient.delete(`/messenger/workgroups/${workgroup.id}/members/${userId}`);
    load();
  }

  async function toggleRole(m: WorkgroupMember) {
    const nextRole = m.role === 'admin' ? 'member' : 'admin';
    await apiClient.patch(`/messenger/workgroups/${workgroup.id}/members/${m.user_id}`, { role: nextRole });
    load();
  }

  async function transferOwnership(userId: string) {
    if (!confirm('Trasferire la proprietà del workgroup a questo membro?')) return;
    await apiClient.patch(`/messenger/workgroups/${workgroup.id}`, { owner_id: userId });
    load();
  }

  if (loading) return <div className="p-8 text-center text-muted-foreground"><Loader2 className="animate-spin inline" /></div>;

  // Merge HR data into members lookup
  const hrMap = new Map(hrDirectory.map(h => [h.id, h]));

  return (
    <div className="p-4 space-y-4">
      {canManage && (
        <div className="bg-card border border-border rounded-xl p-3">
          {!addOpen ? (
            <button
              onClick={() => setAddOpen(true)}
              className="w-full text-sm text-blue-400 hover:text-blue-300 flex items-center justify-center gap-2 py-1"
            >
              <UserPlus size={14} /> Aggiungi membro
            </button>
          ) : (
            <div>
              <div className="flex items-center gap-2 mb-2">
                <Search size={14} className="text-muted-foreground" />
                <input
                  value={query}
                  onChange={(e) => searchUsers(e.target.value)}
                  placeholder="Cerca utenti..."
                  autoFocus
                  className="flex-1 bg-muted border border-border rounded-lg px-3 py-1.5 text-sm text-foreground"
                />
                <button onClick={() => { setAddOpen(false); setQuery(''); }} className="text-muted-foreground hover:text-foreground">
                  <X size={16} />
                </button>
              </div>
              {searchResults.length > 0 && (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {searchResults.filter(u => !members.find(m => m.user_id === u.id)).map(u => (
                    <button
                      key={u.id}
                      onClick={() => addMember(u.id)}
                      className="w-full text-left px-3 py-2 hover:bg-muted rounded text-sm text-foreground"
                    >
                      {u.first_name} {u.last_name} <span className="text-muted-foreground/80">· {u.email}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <div className="space-y-2">
        {members.map(m => {
          const hr = hrMap.get(m.user_id);
          const isOwner = workgroup.owner_id === m.user_id;
          return (
            <div key={m.user_id} className="bg-card border border-border rounded-xl p-3 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center text-foreground text-sm flex-shrink-0 overflow-hidden">
                {(hr?.avatar_url || m.avatar_url) ? (
                  <img src={hr?.avatar_url || m.avatar_url || ''} alt="" className="w-full h-full object-cover" />
                ) : (
                  getInitials(`${m.first_name ?? ''} ${m.last_name ?? ''}`)
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-foreground truncate">
                    {m.first_name} {m.last_name}
                  </span>
                  {isOwner && (
                    <span className="flex items-center gap-1 text-xs text-amber-400">
                      <Crown size={10} /> Owner
                    </span>
                  )}
                  {m.role === 'admin' && !isOwner && (
                    <span className="flex items-center gap-1 text-xs text-blue-400">
                      <Shield size={10} /> Admin
                    </span>
                  )}
                </div>
                <div className="text-xs text-muted-foreground/80 truncate">
                  {hr?.hr_position && <span>{hr.hr_position}</span>}
                  {hr?.hr_department_name && <span> · {hr.hr_department_name}</span>}
                  {hr?.extension && <span> · int. {hr.extension}</span>}
                  {!hr?.hr_position && m.email && <span>{m.email}</span>}
                </div>
              </div>
              {canManage && !isOwner && m.user_id !== currentUserId && (
                <div className="flex items-center gap-1">
                  <button
                    onClick={() => toggleRole(m)}
                    className="text-xs px-2 py-1 text-muted-foreground hover:text-foreground"
                    title={m.role === 'admin' ? 'Rendi membro' : 'Promuovi admin'}
                  >
                    {m.role === 'admin' ? '↓' : '↑'}
                  </button>
                  {workgroup.owner_id === currentUserId && (
                    <button
                      onClick={() => transferOwnership(m.user_id)}
                      className="text-xs px-2 py-1 text-amber-400 hover:text-amber-300"
                      title="Trasferisci proprietà"
                    >
                      <Crown size={12} />
                    </button>
                  )}
                  <button
                    onClick={() => removeMember(m.user_id)}
                    className="text-xs px-2 py-1 text-red-400 hover:text-red-300"
                  >
                    <X size={14} />
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
